/**
 * Export Utilities for VOHK
 * Handles Excel and PDF export functionality
 */

import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const COMPANY_NAME = 'HK Clothiers';

/**
 * Format date for display
 */
const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

/**
 * Format datetime for display
 */
const formatDateTime = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleString('en-IN', { 
    day: '2-digit', 
    month: 'short', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Format number with commas
 */
const formatNumber = (num) => {
  if (num === null || num === undefined || num === '') return '';
  return Number(num).toLocaleString('en-IN', { maximumFractionDigits: 2 });
};

/**
 * Export data to Excel
 */
export const exportToExcel = (data, columns, filename, sheetName = 'Sheet1') => {
  if (!data || data.length === 0) {
    return { success: false, message: 'No data available to export.' };
  }

  try {
    // Create worksheet data with headers
    const headers = columns.map(col => col.header);
    const rows = data.map(item => 
      columns.map(col => {
        let value = col.accessor(item);
        // Format dates
        if (col.type === 'date' && value) {
          value = formatDate(value);
        }
        // Format numbers
        if (col.type === 'number' && value !== null && value !== undefined) {
          value = Number(value);
        }
        return value ?? '';
      })
    );

    const wsData = [headers, ...rows];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Set column widths
    const colWidths = columns.map(col => ({ wch: col.width || 15 }));
    ws['!cols'] = colWidths;

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    // Generate buffer and save
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `${filename}_${formatDateForFilename()}.xlsx`);

    return { success: true, message: `Exported ${data.length} records to Excel` };
  } catch (error) {
    console.error('Excel export error:', error);
    return { success: false, message: 'Failed to export to Excel' };
  }
};

/**
 * Export data to PDF
 */
export const exportToPDF = (data, columns, filename, reportName, filters = []) => {
  if (!data || data.length === 0) {
    return { success: false, message: 'No data available to export.' };
  }

  try {
    const doc = new jsPDF('l', 'mm', 'a4'); // Landscape for more columns
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Header
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(COMPANY_NAME, pageWidth / 2, 15, { align: 'center' });

    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(reportName, pageWidth / 2, 22, { align: 'center' });

    // Date and time
    doc.setFontSize(9);
    doc.text(`Generated: ${formatDateTime(new Date())}`, pageWidth / 2, 28, { align: 'center' });

    // Applied filters
    let startY = 33;
    if (filters && filters.length > 0) {
      doc.setFontSize(8);
      doc.setFont('helvetica', 'italic');
      const filterText = `Filters: ${filters.join(' | ')}`;
      doc.text(filterText, 14, startY);
      startY += 5;
    }

    // Prepare table data
    const headers = columns.map(col => col.header);
    const rows = data.map(item => 
      columns.map(col => {
        let value = col.accessor(item);
        if (col.type === 'date' && value) {
          value = formatDate(value);
        }
        if (col.type === 'number' && value !== null && value !== undefined) {
          value = formatNumber(value);
        }
        return value ?? '';
      })
    );

    // Calculate column widths based on content
    const colWidths = columns.map(col => col.pdfWidth || 'auto');

    // Generate table using autoTable
    autoTable(doc, {
      head: [headers],
      body: rows,
      startY: startY,
      theme: 'grid',
      styles: {
        fontSize: 8,
        cellPadding: 2,
        overflow: 'linebreak',
        halign: 'left'
      },
      headStyles: {
        fillColor: [128, 0, 32], // Maroon color
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'center'
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      columnStyles: columns.reduce((acc, col, idx) => {
        if (col.type === 'number') {
          acc[idx] = { halign: 'right' };
        }
        return acc;
      }, {}),
      margin: { top: 35, left: 14, right: 14 },
      didDrawPage: (data) => {
        // Footer with page numbers
        const pageCount = doc.internal.getNumberOfPages();
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text(
          `Page ${data.pageNumber} of ${pageCount}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' }
        );
        doc.text(
          COMPANY_NAME,
          14,
          pageHeight - 10
        );
        doc.text(
          formatDate(new Date()),
          pageWidth - 14,
          pageHeight - 10,
          { align: 'right' }
        );
      }
    });

    // Save PDF
    doc.save(`${filename}_${formatDateForFilename()}.pdf`);

    return { success: true, message: `Exported ${data.length} records to PDF` };
  } catch (error) {
    console.error('PDF export error:', error);
    return { success: false, message: 'Failed to export to PDF' };
  }
};

/**
 * Format date for filename
 */
const formatDateForFilename = () => {
  const now = new Date();
  return `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
};

/**
 * Column definitions for different export types
 */
export const EXPORT_COLUMNS = {
  orders: [
    { header: 'Order No.', accessor: (o) => o.order_details?.indent_number, width: 12 },
    { header: 'Date', accessor: (o) => o.order_details?.order_date, type: 'date', width: 12 },
    { header: 'Buyer', accessor: (o) => o.buyers?.map(b => b.buyer_name).join(', '), width: 20 },
    { header: 'Supplier', accessor: (o) => o.suppliers?.map(s => s.supplier_name).join(', '), width: 20 },
    { header: 'Status', accessor: (o) => o.order_details?.status, width: 12 },
    { header: 'Total Qty', accessor: (o) => o.total_qty, type: 'number', width: 12 },
    { header: 'Remaining', accessor: (o) => o.remaining_balance, type: 'number', width: 12 },
    { header: 'Deliveries', accessor: (o) => o.deliveries?.length || 0, type: 'number', width: 10 },
  ],

  buyers: [
    { header: 'Sr. No.', accessor: (b, idx) => idx + 1, width: 8 },
    { header: 'Buyer Name', accessor: (b) => b.name, width: 40 },
  ],

  suppliers: [
    { header: 'Sr. No.', accessor: (s, idx) => idx + 1, width: 8 },
    { header: 'Supplier Name', accessor: (s) => s.name, width: 25 },
    { header: 'Address', accessor: (s) => s.address || '', width: 30 },
    { header: 'City', accessor: (s) => s.city || '', width: 15 },
    { header: 'Market', accessor: (s) => s.market || '', width: 15 },
  ],

  tradeNames: [
    { header: 'Sr. No.', accessor: (t, idx) => idx + 1, width: 8 },
    { header: 'Trade Name', accessor: (t) => t.name, width: 25 },
    { header: 'Supplier', accessor: (t) => t.supplier_name || '', width: 20 },
    { header: 'Fabric Type', accessor: (t) => t.fabric_type || '', width: 12 },
    { header: 'Unit', accessor: (t) => t.si_unit || '', width: 10 },
    { header: 'Status', accessor: (t) => t.is_active !== false ? 'Active' : 'Inactive', width: 10 },
  ],

  transporters: [
    { header: 'Sr. No.', accessor: (t, idx) => idx + 1, width: 8 },
    { header: 'Transporter Name', accessor: (t) => t.name, width: 25 },
    { header: 'Source', accessor: (t) => t.source || '', width: 20 },
    { header: 'Destination', accessor: (t) => t.final_destination || '', width: 20 },
    { header: 'Status', accessor: (t) => t.is_active !== false ? 'Active' : 'Inactive', width: 10 },
  ],

  samples: [
    { header: 'Sample ID', accessor: (s) => s.sample_id, width: 12 },
    { header: 'Supplier', accessor: (s) => s.supplier_name, width: 20 },
    { header: 'Trade Name', accessor: (s) => s.trade_name, width: 20 },
    { header: 'GSM', accessor: (s) => s.gsm, width: 10 },
    { header: 'Width', accessor: (s) => s.width, width: 10 },
    { header: 'Date Received', accessor: (s) => s.date_received, type: 'date', width: 15 },
    { header: 'Remarks', accessor: (s) => s.remarks || '', width: 25 },
  ],

  dashboard: [
    { header: 'Metric', accessor: (d) => d.metric, width: 25 },
    { header: 'Value', accessor: (d) => d.value, width: 15 },
  ],

  orderDetail: {
    printedFabrics: [
      { header: 'Trade Name', accessor: (f) => f.trade_name, width: 20 },
      { header: 'Design No.', accessor: (f) => f.design_number, width: 15 },
      { header: 'Qty', accessor: (f) => f.quantity, type: 'number', width: 10 },
      { header: 'Match', accessor: (f) => f.num_matchings, type: 'number', width: 8 },
      { header: 'Total Qty', accessor: (f) => f.total_qty, type: 'number', width: 12 },
      { header: 'P.Rate', accessor: (f) => f.purchase_rate, type: 'number', width: 10 },
      { header: 'P.CD%', accessor: (f) => f.purchase_cd_percent || 0, type: 'number', width: 8 },
      { header: 'P.Amount', accessor: (f) => f.purchase_amount, type: 'number', width: 12 },
      { header: 'S.Rate', accessor: (f) => f.sales_rate, type: 'number', width: 10 },
      { header: 'S.CD%', accessor: (f) => f.sales_cd_percent || 0, type: 'number', width: 8 },
      { header: 'S.Amount', accessor: (f) => f.sales_amount, type: 'number', width: 12 },
      { header: 'Status', accessor: (f) => f.status, width: 10 },
    ],
    dyedFabrics: [
      { header: 'Trade Name', accessor: (f) => f.trade_name, width: 20 },
      { header: 'Color No.', accessor: (c) => c.color_number, width: 12 },
      { header: 'Color Name', accessor: (c) => c.color_name || '', width: 15 },
      { header: 'Qty', accessor: (c) => c.qty, type: 'number', width: 10 },
      { header: 'Unit', accessor: (c) => c.unit, width: 8 },
      { header: 'P.Rate', accessor: (c) => c.purchase_rate, type: 'number', width: 10 },
      { header: 'P.CD%', accessor: (c, idx, fabric) => fabric?.trade_purchase_cd_percent || 0, type: 'number', width: 8 },
      { header: 'P.Amount', accessor: (c) => c.purchase_amount, type: 'number', width: 12 },
      { header: 'S.Rate', accessor: (c) => c.sales_rate, type: 'number', width: 10 },
      { header: 'S.CD%', accessor: (c, idx, fabric) => fabric?.trade_sales_cd_percent || 0, type: 'number', width: 8 },
      { header: 'S.Amount', accessor: (c) => c.sales_amount, type: 'number', width: 12 },
    ],
    deliveries: [
      { header: 'Phase', accessor: (d) => d.phase_number, width: 8 },
      { header: 'Date', accessor: (d) => d.delivery_date, type: 'date', width: 12 },
      { header: 'LR Date', accessor: (d) => d.lr_date, type: 'date', width: 12 },
      { header: 'Transporter', accessor: (d) => d.transporter_name, width: 20 },
      { header: 'Quantity', accessor: (d) => d.total_quantity, type: 'number', width: 12 },
      { header: 'Status', accessor: (d) => d.status, width: 12 },
    ],
  },
};

/**
 * Export order detail to PDF (special case with multiple sections)
 */
export const exportOrderDetailToPDF = (order, deliveredByDesign = {}) => {
  if (!order) {
    return { success: false, message: 'No order data available to export.' };
  }

  try {
    const doc = new jsPDF('l', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Header
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(COMPANY_NAME, pageWidth / 2, 15, { align: 'center' });

    doc.setFontSize(12);
    doc.text(`Order: ${order.order_details?.indent_number}`, pageWidth / 2, 22, { align: 'center' });

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated: ${formatDateTime(new Date())}`, pageWidth / 2, 28, { align: 'center' });

    // Order details
    let startY = 35;
    doc.setFontSize(9);
    doc.text(`Date: ${formatDate(order.order_details?.order_date)}`, 14, startY);
    doc.text(`Status: ${order.order_details?.status}`, 80, startY);
    doc.text(`Buyer: ${order.buyers?.map(b => b.buyer_name).join(', ')}`, 140, startY);
    startY += 5;
    doc.text(`Supplier: ${order.suppliers?.map(s => s.supplier_name).join(', ')}`, 14, startY);
    startY += 8;

    // Printed Fabrics
    if (order.printed_fabrics && order.printed_fabrics.length > 0) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Printed Fabrics', 14, startY);
      startY += 2;

      const printedData = order.printed_fabrics.map(f => [
        f.trade_name,
        f.design_number,
        formatNumber(f.quantity),
        f.num_matchings,
        formatNumber(f.total_qty),
        formatNumber(f.purchase_rate),
        f.purchase_cd_percent ? `${f.purchase_cd_percent}%` : '-',
        formatNumber(f.purchase_amount),
        formatNumber(f.sales_rate),
        f.sales_cd_percent ? `${f.sales_cd_percent}%` : '-',
        formatNumber(f.sales_amount),
        f.status
      ]);

      autoTable(doc, {
        head: [['Trade Name', 'Design No.', 'Qty', 'Match', 'Total', 'P.Rate', 'P.CD%', 'P.Amt', 'S.Rate', 'S.CD%', 'S.Amt', 'Status']],
        body: printedData,
        startY: startY,
        theme: 'grid',
        styles: { fontSize: 7, cellPadding: 1.5 },
        headStyles: { fillColor: [128, 0, 32], textColor: [255, 255, 255], fontStyle: 'bold' },
        margin: { left: 14, right: 14 },
      });

      startY = doc.lastAutoTable.finalY + 8;
    }

    // Dyed Fabrics
    if (order.dyed_fabrics && order.dyed_fabrics.length > 0) {
      // Check if we need a new page
      if (startY > pageHeight - 50) {
        doc.addPage();
        startY = 20;
      }

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Dyed Fabrics', 14, startY);
      startY += 2;

      const dyedData = [];
      order.dyed_fabrics.forEach(fabric => {
        fabric.colors?.forEach(color => {
          dyedData.push([
            fabric.trade_name,
            color.color_number,
            color.color_name || '',
            formatNumber(color.qty),
            color.unit,
            formatNumber(color.purchase_rate),
            fabric.trade_purchase_cd_percent ? `${fabric.trade_purchase_cd_percent}%` : '-',
            formatNumber(color.purchase_amount),
            formatNumber(color.sales_rate),
            fabric.trade_sales_cd_percent ? `${fabric.trade_sales_cd_percent}%` : '-',
            formatNumber(color.sales_amount),
          ]);
        });
      });

      if (dyedData.length > 0) {
        autoTable(doc, {
          head: [['Trade Name', 'Color No.', 'Color Name', 'Qty', 'Unit', 'P.Rate', 'P.CD%', 'P.Amt', 'S.Rate', 'S.CD%', 'S.Amt']],
          body: dyedData,
          startY: startY,
          theme: 'grid',
          styles: { fontSize: 7, cellPadding: 1.5 },
          headStyles: { fillColor: [128, 0, 32], textColor: [255, 255, 255], fontStyle: 'bold' },
          margin: { left: 14, right: 14 },
        });

        startY = doc.lastAutoTable.finalY + 8;
      }
    }

    // Deliveries
    if (order.deliveries && order.deliveries.length > 0) {
      if (startY > pageHeight - 50) {
        doc.addPage();
        startY = 20;
      }

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Deliveries', 14, startY);
      startY += 2;

      const deliveryData = order.deliveries.map(d => [
        d.phase_number,
        formatDate(d.delivery_date),
        formatDate(d.lr_date),
        d.transporter_name || '',
        formatNumber(d.total_quantity),
        d.status
      ]);

      autoTable(doc, {
        head: [['Phase', 'Date', 'LR Date', 'Transporter', 'Quantity', 'Status']],
        body: deliveryData,
        startY: startY,
        theme: 'grid',
        styles: { fontSize: 7, cellPadding: 1.5 },
        headStyles: { fillColor: [128, 0, 32], textColor: [255, 255, 255], fontStyle: 'bold' },
        margin: { left: 14, right: 14 },
      });
    }

    // Add page numbers
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
    }

    doc.save(`Order_${order.order_details?.indent_number?.replace(/\//g, '-')}_${formatDateForFilename()}.pdf`);

    return { success: true, message: 'Order exported to PDF' };
  } catch (error) {
    console.error('Order PDF export error:', error);
    return { success: false, message: 'Failed to export order to PDF' };
  }
};

/**
 * Export order detail to Excel (special case with multiple sheets)
 */
export const exportOrderDetailToExcel = (order) => {
  if (!order) {
    return { success: false, message: 'No order data available to export.' };
  }

  try {
    const wb = XLSX.utils.book_new();

    // Order Info sheet
    const orderInfo = [
      ['Order Number', order.order_details?.indent_number],
      ['Date', formatDate(order.order_details?.order_date)],
      ['Status', order.order_details?.status],
      ['Buyer', order.buyers?.map(b => b.buyer_name).join(', ')],
      ['Supplier', order.suppliers?.map(s => s.supplier_name).join(', ')],
      ['Total Qty', order.total_qty],
      ['Remaining', order.remaining_balance],
    ];
    const wsInfo = XLSX.utils.aoa_to_sheet(orderInfo);
    wsInfo['!cols'] = [{ wch: 15 }, { wch: 40 }];
    XLSX.utils.book_append_sheet(wb, wsInfo, 'Order Info');

    // Printed Fabrics sheet
    if (order.printed_fabrics && order.printed_fabrics.length > 0) {
      const printedHeaders = ['Trade Name', 'Design No.', 'Qty', 'Unit', 'Match', 'Total Qty', 'P.Rate', 'P.CD%', 'P.Amount', 'S.Rate', 'S.CD%', 'S.Amount', 'Status'];
      const printedData = order.printed_fabrics.map(f => [
        f.trade_name, f.design_number, f.quantity, f.si_unit, f.num_matchings, f.total_qty,
        f.purchase_rate, f.purchase_cd_percent || 0, f.purchase_amount, f.sales_rate, f.sales_cd_percent || 0, f.sales_amount, f.status
      ]);
      const wsPrinted = XLSX.utils.aoa_to_sheet([printedHeaders, ...printedData]);
      wsPrinted['!cols'] = printedHeaders.map(() => ({ wch: 12 }));
      XLSX.utils.book_append_sheet(wb, wsPrinted, 'Printed Fabrics');
    }

    // Dyed Fabrics sheet
    if (order.dyed_fabrics && order.dyed_fabrics.length > 0) {
      const dyedHeaders = ['Trade Name', 'Color No.', 'Color Name', 'Qty', 'Unit', 'P.Rate', 'P.CD%', 'P.Amount', 'S.Rate', 'S.CD%', 'S.Amount'];
      const dyedData = [];
      order.dyed_fabrics.forEach(fabric => {
        fabric.colors?.forEach(color => {
          dyedData.push([
            fabric.trade_name, color.color_number, color.color_name || '', color.qty, color.unit,
            color.purchase_rate, fabric.trade_purchase_cd_percent || 0, color.purchase_amount, color.sales_rate, fabric.trade_sales_cd_percent || 0, color.sales_amount
          ]);
        });
      });
      if (dyedData.length > 0) {
        const wsDyed = XLSX.utils.aoa_to_sheet([dyedHeaders, ...dyedData]);
        wsDyed['!cols'] = dyedHeaders.map(() => ({ wch: 12 }));
        XLSX.utils.book_append_sheet(wb, wsDyed, 'Dyed Fabrics');
      }
    }

    // Deliveries sheet
    if (order.deliveries && order.deliveries.length > 0) {
      const delHeaders = ['Phase', 'Date', 'LR Date', 'Transporter', 'Quantity', 'Status'];
      const delData = order.deliveries.map(d => [
        d.phase_number, formatDate(d.delivery_date), formatDate(d.lr_date),
        d.transporter_name || '', d.total_quantity, d.status
      ]);
      const wsDel = XLSX.utils.aoa_to_sheet([delHeaders, ...delData]);
      wsDel['!cols'] = delHeaders.map(() => ({ wch: 15 }));
      XLSX.utils.book_append_sheet(wb, wsDel, 'Deliveries');
    }

    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `Order_${order.order_details?.indent_number?.replace(/\//g, '-')}_${formatDateForFilename()}.xlsx`);

    return { success: true, message: 'Order exported to Excel' };
  } catch (error) {
    console.error('Order Excel export error:', error);
    return { success: false, message: 'Failed to export order to Excel' };
  }
};
